import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import {
  Search,
  Filter,
  Upload,
  FileText,
  Shield,
  Lock,
  Grid,
  List,
  ChevronDown,
  ChevronRight,
  Calendar,
  Folder,
  FolderPlus,
  MoreVertical,
  Pencil,
  Trash2,
  GripVertical,
  FolderInput,
  Loader2,
} from "lucide-react";
import { documentsApi, foldersApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import DocumentDetail from "./DocumentDetail";
import DocumentUploadModal from "./DocumentUploadModal";
import CreateFolderModal from "./CreateFolderModal";
import FolderPermissionsModal from "./FolderPermissionsModal";
import PremiumModal from "../ui/PremiumModal";

interface DocumentData {
  _id: string;
  title: string;
  type: string;
  matterId?: { _id: string; title?: string } | string | null;
  clientId?: { _id: string; name?: string } | string | null;
  description?: string;
  tags?: string[];
  classification?: {
    isPrivileged?: boolean;
    containsPii?: boolean;
    sensitivity?: string;
  };
  currentVersionId?: {
    _id: string;
    file?: {
      filename: string;
      mimeType: string;
      sizeBytes: number;
    };
    storage?: {
      key: string;
    };
  } | null;
  createdAt: string;
  updatedAt?: string;
}

interface FolderData {
  _id: string;
  name: string;
  parentId?: string | null;
  clientId?: string | null;
  matterId?: string | null;
  color?: string;
  itemCount?: number;
  accessLevel?: "all" | "restricted";
  allowedUserIds?: string[];
  createdAt: string;
}

const typeLabels: Record<string, string> = {
  intake_upload: "Intake Upload",
  retainer: "Retainer",
  correspondence: "Correspondence",
  pleading: "Pleading",
  evidence: "Evidence",
  invoice_pdf: "Invoice PDF",
  receipt: "Receipt",
  closing_statement: "Closing Statement",
  title_search: "Title Search",
  mortgage_instructions: "Mortgage Instructions",
  other: "Other",
};

const typeColors: Record<string, string> = {
  retainer: "bg-emerald-100 text-emerald-700",
  pleading: "bg-blue-100 text-blue-700",
  evidence: "bg-purple-100 text-purple-700",
  correspondence: "bg-cyan-100 text-cyan-700",
  invoice_pdf: "bg-amber-100 text-amber-700",
  closing_statement: "bg-indigo-100 text-indigo-700",
  intake_upload: "bg-pink-100 text-pink-700",
  other: "bg-slate-100 text-slate-700",
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const DocumentsPage: React.FC = () => {
  const { userData, firmData } = useUserDataContext();
  const isAdmin = firmData?.adminUserId === userData?._id;

  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  // Folder navigation state
  const [currentFolder, setCurrentFolder] = useState<FolderData | null>(null);
  const [folderPath, setFolderPath] = useState<FolderData[]>([]);
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  // Folder context menu / actions
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const [renamingFolder, setRenamingFolder] = useState<FolderData | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [permissionsFolder, setPermissionsFolder] = useState<FolderData | null>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);

  // Delete folder confirmation
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState<FolderData | null>(null);

  // Drag-drop state
  const [dragItem, setDragItem] = useState<{ id: string; name: string; kind: "folder" | "document" } | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ folderId: string; position: "before" | "into" | "after" } | null>(null);
  const dropTargetRef = useRef<{ folderId: string; position: "before" | "into" | "after" } | null>(null);
  const [dragOverBreadcrumb, setDragOverBreadcrumb] = useState<number | null>(null);
  const [moveIntent, setMoveIntent] = useState<{ item: { id: string; name: string; kind: "folder" | "document" }; targetFolderId: string | null; targetFolderName: string } | null>(null);
  const [moving, setMoving] = useState(false);

  const fetchFolders = useCallback(async () => {
    try {
      const params: Record<string, any> = {
        parentId: currentFolder?._id || "root",
      };
      const res = await foldersApi.list(params);
      setFolders(res.data.data || res.data || []);
    } catch (err) {
      console.error("Error fetching folders:", err);
      setFolders([]);
    }
  }, [currentFolder]);

  const initialLoadRef = React.useRef(true);

  const fetchDocuments = useCallback(async () => {
    if (initialLoadRef.current) setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: 20 };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      params.folderId = currentFolder?._id || "root";
      const res = await documentsApi.list(params);
      setDocuments(res.data.data || []);
      setPagination((prev) => ({
        ...prev,
        pages: res.data.pagination?.pages || 1,
        total: res.data.pagination?.total || 0,
      }));
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
    }
  }, [search, typeFilter, pagination.page, currentFolder]);

  // Seed client/matter folders once, then fetch
  const seededRef = React.useRef(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!seededRef.current) {
      seededRef.current = true;
      foldersApi.seed().catch(() => {}).finally(() => setReady(true));
    }
  }, []);

  useEffect(() => {
    if (ready) {
      fetchDocuments();
      fetchFolders();
    }
  }, [fetchDocuments, fetchFolders, ready]);

  const handleUploadComplete = () => {
    setShowUpload(false);
    fetchDocuments();
  };

  const handleFolderCreated = () => {
    setShowCreateFolder(false);
    fetchFolders();
  };

  const navigateToFolder = (folder: FolderData) => {
    setFolderPath((prev) => [...prev, folder]);
    setCurrentFolder(folder);
    setPagination((p) => ({ ...p, page: 1 }));

  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const target = folderPath[index];
      setFolderPath((prev) => prev.slice(0, index + 1));
      setCurrentFolder(target);
    }
    setPagination((p) => ({ ...p, page: 1 }));
  };

  // Close folder context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target as Node)) {
        setFolderMenuId(null);
      }
    };
    if (folderMenuId) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [folderMenuId]);

  const handleRenameFolder = async () => {
    if (!renamingFolder || !renameValue.trim()) return;
    try {
      await foldersApi.update(renamingFolder._id, { name: renameValue.trim() });
      setRenamingFolder(null);
      setRenameValue("");
      fetchFolders();
    } catch (err) {
      console.error("Failed to rename folder:", err);
    }
  };

  const handleDeleteFolder = async (folder: FolderData) => {
    try {
      await foldersApi.remove(folder._id);
      setDeleteFolderConfirm(null);
      fetchFolders();
      fetchDocuments();
    } catch (err) {
      console.error("Failed to delete folder:", err);
    }
  };

  // ─── Drag-Drop Handlers ──────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, item: { id: string; name: string; kind: "folder" | "document" }) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  const handleDragOverFolder = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragItem?.kind === "folder" && dragItem.id === folderId) return;

    // Detect position within the folder element for reorder vs move-into
    if (dragItem?.kind === "folder") {
      const rect = e.currentTarget.getBoundingClientRect();
      let position: "before" | "into" | "after";

      if (viewMode === "grid") {
        // Grid view: detect closest edge (top/bottom/left/right)
        const xRatio = (e.clientX - rect.left) / rect.width;
        const yRatio = (e.clientY - rect.top) / rect.height;
        const distLeft = xRatio;
        const distRight = 1 - xRatio;
        const distTop = yRatio;
        const distBottom = 1 - yRatio;
        const minDist = Math.min(distLeft, distRight, distTop, distBottom);

        if (minDist > 0.3) {
          position = "into";
        } else if (minDist === distLeft || minDist === distTop) {
          position = "before";
        } else {
          position = "after";
        }
      } else {
        // List view: Y-axis only
        const ratio = (e.clientY - rect.top) / rect.height;
        if (ratio < 0.4) position = "before";
        else if (ratio > 0.6) position = "after";
        else position = "into";
      }

      const target = { folderId, position };
      dropTargetRef.current = target;
      setDropTarget(target);
    }

    setDragOverFolderId(folderId);
  };

  const handleDragOverBreadcrumb = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverBreadcrumb(index);
  };

  const handleDragLeaveFolder = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(related)) return;
    setDragOverFolderId(null);
    dropTargetRef.current = null;
    setDropTarget(null);
  };

  const handleDragLeaveBreadcrumb = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(related)) return;
    setDragOverBreadcrumb(null);
  };

  const handleDropOnFolder = (e: React.DragEvent, targetFolder: FolderData) => {
    e.preventDefault();
    // Read from ref for reliable timing (state may lag behind)
    const currentDropTarget = dropTargetRef.current;
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
    dropTargetRef.current = null;
    setDropTarget(null);
    if (!dragItem) return;
    if (dragItem.kind === "folder" && dragItem.id === targetFolder._id) return;

    // If dragging a folder and dropping at before/after position, reorder instead of move-into
    if (dragItem.kind === "folder" && currentDropTarget && currentDropTarget.position !== "into") {
      handleReorder(dragItem.id, targetFolder._id, currentDropTarget.position);
      return;
    }

    setMoveIntent({ item: dragItem, targetFolderId: targetFolder._id, targetFolderName: targetFolder.name });
  };

  const handleReorder = async (draggedId: string, targetId: string, position: "before" | "after") => {
    const currentOrder = [...folders];
    const draggedIndex = currentOrder.findIndex((f) => f._id === draggedId);
    if (draggedIndex < 0) return;

    // Remove dragged item
    const [dragged] = currentOrder.splice(draggedIndex, 1);

    // Find target index in the updated array (after removal)
    const targetIndex = currentOrder.findIndex((f) => f._id === targetId);
    if (targetIndex < 0) return;

    // Insert at the right position
    const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
    currentOrder.splice(insertIndex, 0, dragged);

    // Update local state immediately for smooth animation
    setFolders(currentOrder);

    // Persist to backend
    const folderIds = currentOrder.map((f) => f._id);
    try {
      await foldersApi.reorder(folderIds);
    } catch (err) {
      console.error("Reorder failed:", err);
      fetchFolders();
    }
  };

  const handleDropOnBreadcrumb = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
    if (!dragItem) return;
    const targetFolderId = index < 0 ? null : folderPath[index]._id;
    const targetFolderName = index < 0 ? "Documents" : folderPath[index].name;
    if (targetFolderId === currentFolder?._id) return;
    setMoveIntent({ item: dragItem, targetFolderId, targetFolderName });
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDragOverFolderId(null);
    setDragOverBreadcrumb(null);
    dropTargetRef.current = null;
    setDropTarget(null);
  };

  const executeMove = async () => {
    if (!moveIntent) return;
    setMoving(true);
    try {
      if (moveIntent.item.kind === "folder") {
        await foldersApi.update(moveIntent.item.id, { parentId: moveIntent.targetFolderId || null });
      } else {
        await documentsApi.update(moveIntent.item.id, { folderId: moveIntent.targetFolderId || null });
      }
      setMoveIntent(null);
      fetchFolders();
      fetchDocuments();
    } catch (err) {
      console.error("Move failed:", err);
    } finally {
      setMoving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#1F2937" }}>
            Documents
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your firm's documents and files
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "var(--color-primary)";
          }}
        >
          <Upload className="h-4 w-4" />
          Upload Document
        </button>
      </motion.div>

      {/* Breadcrumb Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-slate-200 px-5 py-3 mb-6"
      >
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <Folder className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <button
            onClick={() => navigateToBreadcrumb(-1)}
            onDragOver={(e) => handleDragOverBreadcrumb(e, -1)}
            onDragLeave={handleDragLeaveBreadcrumb}
            onDrop={(e) => handleDropOnBreadcrumb(e, -1)}
            className={`font-medium px-1.5 py-0.5 rounded-md transition-all ${
              dragOverBreadcrumb === -1
                ? "bg-emerald-100 text-emerald-700 scale-105"
                : folderPath.length === 0
                ? "text-slate-800"
                : "text-slate-500 hover:text-brand"
            }`}
          >
            Documents
          </button>
          {folderPath.map((folder, idx) => (
            <React.Fragment key={folder._id}>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
              <button
                onClick={() => navigateToBreadcrumb(idx)}
                onDragOver={(e) => handleDragOverBreadcrumb(e, idx)}
                onDragLeave={handleDragLeaveBreadcrumb}
                onDrop={(e) => handleDropOnBreadcrumb(e, idx)}
                className={`font-medium px-1.5 py-0.5 rounded-md truncate transition-all ${
                  dragOverBreadcrumb === idx
                    ? "bg-emerald-100 text-emerald-700 scale-105"
                    : idx === folderPath.length - 1
                    ? "text-slate-800"
                    : "text-slate-500 hover:text-brand"
                }`}
              >
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
        <button
          onClick={() => setShowCreateFolder(true)}
          className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-colors flex-shrink-0 ml-4"
        >
          <FolderPlus className="h-4 w-4" />
          New Folder
        </button>
      </motion.div>

      {/* Upload Dropzone Area */}
      {!showUpload && (documents.length > 0 || folders.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          onClick={() => setShowUpload(true)}
          className="mb-6 border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-brand-accent hover:bg-brand-light/50 transition-colors"
        >
          <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-500">
            Drag and drop files here, or click to upload
          </p>
        </motion.div>
      )}

      {/* Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap items-center gap-3 mb-6"
      >
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowTypeDropdown(!showTypeDropdown)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:border-slate-300 transition-colors"
          >
            <Filter className="h-4 w-4" />
            {typeFilter ? typeLabels[typeFilter] || typeFilter : "All Types"}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {showTypeDropdown && (
            <div className="absolute top-full mt-1 right-0 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[200px]">
              <button
                onClick={() => {
                  setTypeFilter("");
                  setShowTypeDropdown(false);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
              >
                All Types
              </button>
              {Object.entries(typeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => {
                    setTypeFilter(key);
                    setShowTypeDropdown(false);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"}`}
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </motion.div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--color-primary)" }} />
        </div>
      ) : documents.length === 0 && folders.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <Folder className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            {currentFolder ? "This folder is empty" : "No documents yet"}
          </h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            {currentFolder
              ? "Upload a document or create a subfolder to organize your files in this folder."
              : "Upload your first document to get started. Organize files into folders, tag them by type, and control access with permissions."}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--color-primary)";
              }}
            >
              <Upload className="h-4 w-4" />
              Upload Document
            </button>
            <button
              onClick={() => setShowCreateFolder(true)}
              className="inline-flex items-center gap-2 px-5 py-3 text-slate-700 font-medium bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <FolderPlus className="h-4 w-4" />
              New Folder
            </button>
          </div>
        </motion.div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <LayoutGroup>
            <AnimatePresence>
              {/* Folder Cards */}
              {folders.map((folder) => {
                const isDropBefore = dropTarget?.folderId === folder._id && dropTarget.position === "before";
                const isDropAfter = dropTarget?.folderId === folder._id && dropTarget.position === "after";
                const isDropInto = dropTarget?.folderId === folder._id && dropTarget.position === "into";
                return (
                  <motion.div
                    key={`folder-${folder._id}`}
                    layout
                    transition={{ type: "spring", stiffness: 400, damping: 28 }}
                    data-tour={
                      !currentFolder ? `folder-${folder.name}` :
                      folder.matterId ? `folder-matter-${folder.matterId}` :
                      folder.clientId ? `folder-client-${folder.clientId}` :
                      undefined
                    }
                    draggable
                    onDragStart={(e: any) => handleDragStart(e, { id: folder._id, name: folder.name, kind: "folder" })}
                    onDragOver={(e: any) => handleDragOverFolder(e, folder._id)}
                    onDragLeave={handleDragLeaveFolder as any}
                    onDrop={(e: any) => handleDropOnFolder(e, folder)}
                    onDragEnd={handleDragEnd}
                    onClick={() => {
                      if (renamingFolder?._id !== folder._id) navigateToFolder(folder);
                    }}
                    className="relative"
                  >
                    {/* Drop indicator — before (left edge in grid) */}
                    {isDropBefore && (
                      <div className="absolute -left-2 top-0 bottom-0 z-10 flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-brand -mt-1" />
                        <div className="flex-1 w-[3px] bg-brand rounded-full" />
                        <div className="w-2 h-2 rounded-full bg-brand -mb-1" />
                      </div>
                    )}

                    <div
                      className={`bg-amber-50 rounded-xl border shadow-sm p-5 cursor-grab active:cursor-grabbing hover:shadow-md transition-all relative h-full ${
                        isDropInto
                          ? "ring-2 ring-emerald-500 bg-emerald-50 border-emerald-300 scale-[1.03]"
                          : isDropBefore || isDropAfter
                          ? "border-brand/40"
                          : dragItem?.id === folder._id
                          ? "opacity-40 border-amber-200"
                          : "border-amber-200 hover:scale-[1.02]"
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                          <Folder className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {renamingFolder?._id === folder._id ? (
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleRenameFolder();
                                if (e.key === "Escape") setRenamingFolder(null);
                              }}
                              onBlur={handleRenameFolder}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1 text-sm font-semibold rounded-lg border border-amber-300 focus:outline-none focus:border-emerald-500 bg-white"
                            />
                          ) : (
                            <h3 className="font-semibold text-sm truncate" style={{ color: "#1F2937" }}>
                              {folder.name}
                            </h3>
                          )}
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <p className="text-xs text-slate-500">
                              {folder.itemCount != null
                                ? `${folder.itemCount} item${folder.itemCount !== 1 ? "s" : ""}`
                                : "Folder"}
                            </p>
                            {folder.accessLevel === "restricted" && (
                              <Lock className="h-3 w-3 text-slate-400" />
                            )}
                          </div>
                        </div>
                        {/* Context menu button */}
                        <div className="relative" ref={folderMenuId === folder._id ? folderMenuRef : undefined}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuId(folderMenuId === folder._id ? null : folder._id);
                            }}
                            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-amber-100 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {folderMenuId === folder._id && (
                            <div className="absolute right-0 top-8 z-20 w-44 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-in fade-in slide-in-from-top-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRenamingFolder(folder);
                                  setRenameValue(folder.name);
                                  setFolderMenuId(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Rename
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPermissionsFolder(folder);
                                    setFolderMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                  <Shield className="h-3.5 w-3.5" />
                                  Manage Access
                                </button>
                              )}
                              <div className="border-t border-slate-100 my-1" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFolderMenuId(null);
                                  setDeleteFolderConfirm(folder);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(folder.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Drop indicator — after (right edge in grid) */}
                    {isDropAfter && (
                      <div className="absolute -right-2 top-0 bottom-0 z-10 flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-brand -mt-1" />
                        <div className="flex-1 w-[3px] bg-brand rounded-full" />
                        <div className="w-2 h-2 rounded-full bg-brand -mb-1" />
                      </div>
                    )}
                  </motion.div>
                );
              })}

            {/* Document Cards */}
            {documents.map((doc) => (
              <div
                key={doc._id}
                draggable
                onDragStart={(e) => handleDragStart(e, { id: doc._id, name: doc.title, kind: "document" })}
                onDragEnd={handleDragEnd}
                onClick={() => setSelectedDoc(doc._id)}
                className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${
                  dragItem?.id === doc._id ? "opacity-40" : "hover:scale-[1.02]"
                }`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                    <FileText className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate" style={{ color: "#1F2937" }}>
                      {doc.title}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {doc.matterId && typeof doc.matterId === "object"
                        ? doc.matterId.title || "Unknown matter"
                        : "No matter"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[doc.type] || typeColors.other}`}
                  >
                    {typeLabels[doc.type] || doc.type}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(doc.createdAt)}
                  </span>
                  {doc.currentVersionId && typeof doc.currentVersionId === "object" && doc.currentVersionId.file && (
                    <span>{formatBytes(doc.currentVersionId.file.sizeBytes)}</span>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mt-3">
                  {doc.classification?.isPrivileged && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                      <Shield className="h-3 w-3" />
                      Privileged
                    </span>
                  )}
                  {doc.classification?.sensitivity === "confidential" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-xs font-medium">
                      <Lock className="h-3 w-3" />
                      Confidential
                    </span>
                  )}
                  {doc.classification?.sensitivity === "highly_confidential" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                      <Lock className="h-3 w-3" />
                      Highly Confidential
                    </span>
                  )}
                </div>
              </div>
            ))}
            </AnimatePresence>
          </LayoutGroup>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Title</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Matter</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Size</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-10"></th>
              </tr>
            </thead>
            <tbody>
              {/* Folder rows */}
              {folders.map((folder) => {
                const isDropBefore = dropTarget?.folderId === folder._id && dropTarget.position === "before";
                const isDropAfter = dropTarget?.folderId === folder._id && dropTarget.position === "after";
                const isDropInto = dropTarget?.folderId === folder._id && dropTarget.position === "into";
                return (
                <tr
                  key={`folder-${folder._id}`}
                  data-tour={
                    !currentFolder ? `folder-${folder.name}` :
                    folder.matterId ? `folder-matter-${folder.matterId}` :
                    folder.clientId ? `folder-client-${folder.clientId}` :
                    undefined
                  }
                  draggable
                  onDragStart={(e) => handleDragStart(e, { id: folder._id, name: folder.name, kind: "folder" })}
                  onDragOver={(e) => handleDragOverFolder(e, folder._id)}
                  onDragLeave={handleDragLeaveFolder}
                  onDrop={(e) => handleDropOnFolder(e, folder)}
                  onDragEnd={handleDragEnd}
                  onClick={() => {
                    if (renamingFolder?._id !== folder._id) navigateToFolder(folder);
                  }}
                  className={`border-b cursor-grab active:cursor-grabbing transition-all relative ${
                    isDropInto
                      ? "ring-2 ring-inset ring-emerald-500 bg-emerald-50 border-slate-100"
                      : isDropBefore
                      ? "border-t-2 border-t-brand border-b-slate-100"
                      : isDropAfter
                      ? "border-b-2 border-b-brand"
                      : dragItem?.id === folder._id
                      ? "opacity-40 border-slate-100"
                      : "hover:bg-amber-50/50 border-slate-100"
                  }`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Folder className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      {renamingFolder?._id === folder._id ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRenameFolder();
                            if (e.key === "Escape") setRenamingFolder(null);
                          }}
                          onBlur={handleRenameFolder}
                          onClick={(e) => e.stopPropagation()}
                          className="px-2 py-1 text-sm font-medium rounded-lg border border-amber-300 focus:outline-none focus:border-emerald-500 bg-white"
                        />
                      ) : (
                        <span className="text-sm font-medium" style={{ color: "#1F2937" }}>
                          {folder.name}
                        </span>
                      )}
                      {folder.accessLevel === "restricted" && (
                        <Lock className="h-3 w-3 text-slate-400" />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      Folder
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">--</td>
                  <td className="px-5 py-3 text-sm text-slate-500">
                    {formatDate(folder.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">
                    {folder.itemCount != null ? `${folder.itemCount} items` : "--"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="relative" ref={folderMenuId === folder._id ? folderMenuRef : undefined}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuId(folderMenuId === folder._id ? null : folder._id);
                        }}
                        className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {folderMenuId === folder._id && (
                        <div className="absolute right-0 top-8 z-20 w-44 bg-white rounded-xl shadow-lg border border-slate-200 py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingFolder(folder);
                              setRenameValue(folder.name);
                              setFolderMenuId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Rename
                          </button>
                          {isAdmin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPermissionsFolder(folder);
                                setFolderMenuId(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <Shield className="h-3.5 w-3.5" />
                              Manage Access
                            </button>
                          )}
                          <div className="border-t border-slate-100 my-1" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuId(null);
                              handleDeleteFolder(folder);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}

              {/* Document rows */}
              {documents.map((doc) => (
                <tr
                  key={doc._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, { id: doc._id, name: doc.title, kind: "document" })}
                  onDragEnd={handleDragEnd}
                  onClick={() => setSelectedDoc(doc._id)}
                  className={`border-b border-slate-100 cursor-grab active:cursor-grabbing transition-all ${
                    dragItem?.id === doc._id ? "opacity-40" : "hover:bg-slate-50"
                  }`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm font-medium" style={{ color: "#1F2937" }}>
                        {doc.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[doc.type] || typeColors.other}`}>
                      {typeLabels[doc.type] || doc.type}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-600">
                    {doc.matterId && typeof doc.matterId === "object"
                      ? doc.matterId.title || "--"
                      : "--"}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">
                    {formatDate(doc.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">
                    {doc.currentVersionId && typeof doc.currentVersionId === "object" && doc.currentVersionId.file
                      ? formatBytes(doc.currentVersionId.file.sizeBytes)
                      : "--"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {doc.classification?.isPrivileged && (
                        <Shield className="h-3.5 w-3.5 text-amber-600" />
                      )}
                      {(doc.classification?.sensitivity === "confidential" || doc.classification?.sensitivity === "highly_confidential") && (
                        <Lock className="h-3.5 w-3.5 text-red-600" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
            disabled={pagination.page === 1}
            className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-xl disabled:opacity-50 hover:bg-slate-50 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
            disabled={pagination.page === pagination.pages}
            className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-xl disabled:opacity-50 hover:bg-slate-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <DocumentUploadModal
            onClose={() => setShowUpload(false)}
            onComplete={handleUploadComplete}
            folderId={currentFolder?._id}
          />
        )}
      </AnimatePresence>

      {/* Create Folder Modal */}
      <CreateFolderModal
        open={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        onCreated={handleFolderCreated}
        parentId={currentFolder?._id}
      />

      {/* Document Detail Slide-over */}
      <AnimatePresence>
        {selectedDoc && (
          <DocumentDetail
            documentId={selectedDoc}
            onClose={() => setSelectedDoc(null)}
            onRefresh={fetchDocuments}
          />
        )}
      </AnimatePresence>

      {/* Move Confirmation Modal */}
      <PremiumModal
        open={!!moveIntent}
        onClose={() => setMoveIntent(null)}
        title="Move Item"
        icon={FolderInput}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setMoveIntent(null)}
              disabled={moving}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={executeMove}
              disabled={moving}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                if (!moving) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                if (!moving) e.currentTarget.style.backgroundColor = "var(--color-primary)";
              }}
            >
              {moving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  <FolderInput className="h-4 w-4" />
                  Move
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            Move <span className="font-semibold">"{moveIntent?.item.name}"</span> into{" "}
            <span className="font-semibold">"{moveIntent?.targetFolderName}"</span>?
          </p>
          <p className="text-xs text-slate-400">
            This can be undone by dragging the item back.
          </p>
        </div>
      </PremiumModal>

      {/* Delete Folder Confirmation Modal */}
      <PremiumModal
        open={!!deleteFolderConfirm}
        onClose={() => setDeleteFolderConfirm(null)}
        title="Delete Folder"
        icon={Trash2}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setDeleteFolderConfirm(null)}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteFolderConfirm && handleDeleteFolder(deleteFolderConfirm)}
              className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
            >
              Delete
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Delete <span className="font-semibold">"{deleteFolderConfirm?.name}"</span>? Documents inside will be moved to the parent folder.
        </p>
      </PremiumModal>

      {/* Folder Permissions Modal */}
      {permissionsFolder && (
        <FolderPermissionsModal
          open={!!permissionsFolder}
          onClose={() => setPermissionsFolder(null)}
          folderId={permissionsFolder._id}
          folderName={permissionsFolder.name}
          currentAccessLevel={permissionsFolder.accessLevel || "all"}
          currentAllowedUserIds={permissionsFolder.allowedUserIds || []}
          onUpdated={fetchFolders}
        />
      )}
    </div>
  );
};

export default DocumentsPage;
